import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Modal } from 'jl-design-system/components/modal/Modal';
import Checkbox from 'jl-design-system/components/checkbox/Checkbox';

import {
  errorMessagesActions,
  errorTypes,
  Messages,
  scrollToMessageTypeAnchor,
} from '~/components';
import { PatchTestModalContent } from '~/components/patch-test-section/patch-test-modal-content';

import styles from './PatchTestSection.scss';
import { appointmentDraftSelectors } from '~/api';

const errorMessage = `PatchTest:${errorTypes.QUESTION_FIELD_ERROR}`;

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(() => (
  <Messages forTypes={[`PatchTest:${errorTypes.QUESTION_FIELD_ERROR}`]} />
));

export class PatchTestSection extends PureComponent {
  state = {
    isModalOpen: false,
    patchTestValue: this.props.initialValue || '',
    touched: this.props.error ? true : false,
  };

  componentDidMount() {
    this.updateErrorMessages();
  }

  componentDidUpdate(prevProps, prevState) {
    const prevHasError = !prevState.patchTestValue && prevState.touched;
    const currentHasError = !this.state.patchTestValue && this.state.touched;

    // Mark as touched if error prop is passed from form validation
    if (this.props.error && !this.state.touched) {
      this.setState({ touched: true });
    }

    // Only update error messages if the validation errors actually changed
    if (prevHasError !== currentHasError) {
      this.updateErrorMessages();
    }
  }

  updateErrorMessages = () => {
    const {
      dispatchRemoveSingleErrorMessage,
      dispatchPushSectionError,
      dispatchClearSectionErrors,
    } = this.props;

    const { patchTestValue, touched } = this.state;
    const isSectionInvalid = touched && !patchTestValue;

    dispatchClearSectionErrors('patchTest');
    if (isSectionInvalid) {
      dispatchPushSectionError(`${errorMessage}:patchTest`);
    } else {
      dispatchRemoveSingleErrorMessage(errorMessage);
    }
  };

  handleOpenModal = () => {
    this.setState({ isModalOpen: true });
  };

  handleCloseModal = () => {
    this.setState({ isModalOpen: false });
  };

  handleCheckboxChange = (e) => {
    const value = e.target.checked ? 'true' : '';
    this.setState({ patchTestValue: value, touched: true }, () => {
      const { onFieldChange } = this.props;
      if (onFieldChange) {
        onFieldChange('patchTestSection.patchTest', value);
      }
    });
  };

  render() {
    const { contactPhone } = this.props;
    const { isModalOpen, patchTestValue, touched } = this.state;
    const showError = touched && !patchTestValue;

    return (
      <div className={styles.PatchTestSection}>
        <ScrollToMessageTypeAnchor forTypes={[errorMessage]} />
        <section className={styles.PatchTestSection__info}>
          <div id="title" className={styles.PatchTestSection__info__title}>
            <span className={styles.PatchTestSection__info__icon} />
            Patch test required
          </div>
          <p>
            For this appointment you are required to have had the necessary patch test in the last 6
            months. If you haven&apos;t yet, you will need to have one prior to your appointment.
            <br />
            <button type="button" onClick={this.handleOpenModal}>
              Why do I need this?
            </button>
            <Modal
              isOpen={isModalOpen}
              onClose={this.handleCloseModal}
              title="Patch test"
              rootId="page-root"
              footer={false}
            >
              <PatchTestModalContent branchPhone={contactPhone} />
            </Modal>
          </p>
          <div className={showError ? styles.PatchTestSection__error : ''}>
            <Checkbox
              name="patchTest"
              id="ocab-patch-test"
              checked={!!patchTestValue}
              onChange={this.handleCheckboxChange}
              required
              label="I've had a patch test in the last 6 months or I'll arrange to have one before the appointment"
              error={showError}
            />
            {showError && (
              <div className={styles.PatchTestSection__error_message}>
                Please confirm if you&apos;ve had or will have the necessary patch test
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    router: { location },
  } = state;

  return {
    displayPatchTestModal:
      location.state && location.state.patchTestModal && location.state.patchTestModal.isOpen,
    location,
    contactPhone: appointmentDraftSelectors.selectContactPhone(state),
  };
};

const mapDispatchToProps = {
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchPushSectionError: errorMessagesActions.pushSectionError,
  dispatchClearSectionErrors: errorMessagesActions.clearSectionErrors,
};

export const ConnectedPatchTestSection = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PatchTestSection),
);

PatchTestSection.propTypes = {
  dispatchRemoveSingleErrorMessage: PropTypes.func,
  dispatchPushSectionError: PropTypes.func,
  dispatchClearSectionErrors: PropTypes.func,
  onFieldChange: PropTypes.func,
  initialValue: PropTypes.string,
  history: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({}).isRequired,
  displayPatchTestModal: PropTypes.bool,
  contactPhone: PropTypes.string.isRequired,
  error: PropTypes.string,
};

PatchTestSection.defaultProps = {
  displayPatchTestModal: false,
  initialValue: '',
};
